import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Background } from "../../../shared/ui/background";
import { Modal } from "../../../shared/ui/modal";
import { NavBar } from "../../../shared/ui/navbar";
import { BonusResultModal } from "../../ui/bonus-result";
import { GameplayLayout } from "../../ui/gameplay-layout";
import { PrizeQuestion } from "../../ui/question-prize";
import "./gameplay-bonus.scss";
import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { Player, PlayerProps } from "../../../shared/ui/player";
import { bonusResultContainerVariants } from "../../utils/animation-states";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGameEvents } from "../../../shared/data-access/store/game/hooks/use-game-events";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { selectUser } from "../../../auth/data-access/store/authSlice";
import { selectLanguage } from "../../../shared/data-access/store/general/generalSlice";
import { GameplayAnswer, GameplayAnswerPlayer } from "../../ui/gameplay-answer";
import { setMessage } from "../../../shared/data-access/store/message/messageSlice";
import { useCountdown } from "../../../utils/hooks/use-countdown";
import { Badge, BadgeProps } from "../../../shared/ui/badge";
import { secondsToMilliseconds } from "date-fns";
import LoadingOpponent from "../../../shared/ui/loading-opponent/loading-opponent";
import { differenceInMilliseconds } from "date-fns/esm";
import { reset } from "../../../shared/data-access/store/game/gameSlices";
import { SocketResponseError } from "../../../shared/data-access/store/game/types/requests";
import { useLazyGetUserQuery } from "../../../auth/data-access/store/services/auth.service";
import { TIMER_ANIMATION_DELAY } from "../../../utils/constants";
import shuffleAnswers from "../../utils/shuffle-answers";
import SoundManager from "../../../utils/managers/sound-manager";

export function BonusGameplay() {
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const id = searchParams.get("id");
  const { send: initGame, meta, data: game } = useGameEvents("init_bonus_game");
  const { send: getBonusOpponent, data: opponentData } =
    useGameEvents("get_bonus_opponent");
  const { send: startGame, data: startGameData } =
    useGameEvents("start_bonus_game");
  const { send: finishGame, data: gameResults } =
    useGameEvents("finish_bonus_game");

  const [getUser] = useLazyGetUserQuery();

  const results = gameResults?.data;
  const correctAnswer = results?.answer ?? false;
  const playerAnswer = results?.answer;
  const opponentAnswer = results?.opponent_answer;

  const lang = useAppSelector(selectLanguage);

  const { question: questionData } = startGameData?.data || {};
  const question = useMemo(
    () => (questionData ? shuffleAnswers(questionData) : undefined),
    [questionData]
  );
  const user = useAppSelector(selectUser);
  const opponent = opponentData?.data?.opponent;

  const timer = question?.timer;

  const { timeLeft, start, stop, getUntilDateTime, clean } = useCountdown(
    timer && timer + TIMER_ANIMATION_DELAY
  );

  const { token, uuid } = meta;
  const currentTimeRef = useRef<number>();
  const answeredRef = useRef(false);
  const opponentRef = useRef(opponent);
  const [opponentAnswered, setOpponentAnswered] = useState(false);

  const formatAnswerTime = (ms: number) => {
    return (ms / 1000).toFixed(2);
  };

  const commonParams = useMemo(() => {
    if (!id) {
      return false;
    }
    return {
      playedBonusGame: {
        id,
      },
      userToken: token,
      uuid,
    };
  }, [id, token, uuid]);

  const handleError = (error: unknown) => {
    const message =
      (error as Error).message ??
      (error as { error: Error }).error.message ??
      (error as { error: { error: string } }).error.error ??
      error ??
      "Unknown error";
    if ((error as SocketResponseError).error_header === 400) {
      dispatch(
        setMessage({
          content: message.includes("synchroniz")
            ? "Oops, this game has expired"
            : "Oops, something went wrong",
          type: "error",
        })
      );
    } else {
      dispatch(
        setMessage({
          content: "Oops, something went wrong",
          type: "error",
        })
      );
    }

    setTimeout(() => {
      dispatch(reset());
      navigate("/");
    }, 3000);
  };

  const getAnswerType = (index: number): GameplayAnswerPlayer | undefined => {
    const playerAnswerIndex = results?.answer.answer_index;
    const opponentAnswerIndex = results?.opponent_answer.answer_index;
    if (
      playerAnswerIndex === index &&
      playerAnswerIndex === opponentAnswerIndex &&
      opponentAnswered
    ) {
      return "both";
    }

    if (playerAnswerIndex === index) {
      return "user";
    }

    if (opponentAnswerIndex === index && opponentAnswered) {
      return "opponent";
    }
  };

  const handleAnswer = useCallback(
    async (index: number) => {
      const untilDateTime = getUntilDateTime();
      if (
        !commonParams ||
        !untilDateTime ||
        !timer ||
        currentTimeRef.current === undefined ||
        answeredRef.current
      ) {
        return;
      }

      answeredRef.current = true;
      clean();

      try {
        await finishGame({
          ...commonParams,
          answer: index,
          answer_time: Math.abs(
            timer * 1000 - differenceInMilliseconds(untilDateTime, new Date())
          ),
        });
      } catch (e) {
        handleError(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commonParams, getUntilDateTime, timer]
  );

  const isImageQuestion = () =>
    question && question.answers[0].type === "image";

  useEffect(() => {
    if (!id) {
      navigate("/");
    } else {
      (async () => {
        try {
          if (commonParams) {
            await initGame(commonParams);
          }
        } catch (error: unknown) {
          handleError(error);

          setTimeout(() => {
            navigate("/");
            dispatch(reset());
          }, 3000);
        }
      })();
    }

    return () => {
      getUser();
    };
  }, []);

  useEffect(() => {
    opponentRef.current = opponent;
  }, [opponent]);

  useEffect(() => {
    if (!results) {
      return;
    }

    const playerTime = results.answer.answer_time;
    const opponentTime = results.opponent_answer.answer_time;
    const showReward = () => {
      setTimeout(() => {
        setShowInfo(true);
      }, 4000);
    };

    if (playerTime < opponentTime) {
      const timeout = setTimeout(() => {
        setOpponentAnswered(true);
        stop();
        showReward();
      }, opponentTime - playerTime);

      return () => {
        clearTimeout(timeout);
      };
    } else {
      setOpponentAnswered(true);
      stop();
      showReward();
    }
  }, [results]);

  useEffect(() => {
    if (timeLeft === undefined) {
      return;
    }

    if (timeLeft < 5) {
      SoundManager.vfxSounds.clock.playbackRate = 1.3;
    }

    if (timeLeft > 0) {
      currentTimeRef.current = timeLeft;
      SoundManager.vfxSounds.clock.play();
    } else {
      if (timeLeft === 0) {
        if (currentTimeRef.current && currentTimeRef.current > 0) {
          currentTimeRef.current = 0;
        }
      } else {
        if (!currentTimeRef.current) {
          currentTimeRef.current = 0;
        }
        stop();
        handleAnswer(-1);
      }
    }
  }, [timeLeft]);

  useEffect(() => {
    if (!opponent?.id) {
      return;
    }

    (async () => {
      try {
        if (commonParams) {
          await startGame(commonParams);
          start();
        }
      } catch (error) {
        handleError(error);
      }
    })();
  }, [opponent?.id]);

  useEffect(() => {
    if (!commonParams) {
      return;
    }

    const timeout = setTimeout(async () => {
      if (opponentRef.current?.id) {
        return;
      }

      try {
        await getBonusOpponent(commonParams);
      } catch (error) {
        handleError(error);
      }
    }, secondsToMilliseconds(5));

    return () => {
      clearTimeout(timeout);
    };
  }, [commonParams]);

  useEffect(() => {
    if (showInfo && results) {
      if (results.won && results.answer.correct) {
        SoundManager.vfxSounds.win.play();
      } else {
        SoundManager.vfxSounds.lose.play();
      }
    }
  }, [showInfo, results]);

  useEffect(() => {
    if (answeredRef.current) {
      SoundManager.vfxSounds.clock.pause();
      SoundManager.vfxSounds.clock.playbackRate = 1;
      SoundManager.vfxSounds.clock.currentTime = 0;
    }
  }, [answeredRef.current]);

  const handleContinue = useCallback((showModal: boolean) => {
    navigate("/");
    dispatch(reset());
    setShowInfo(showModal);
  }, []);

  const generatePrizeDescription = (
    prizes: Array<{ type: string; value: number | string }>
  ) => {
    const filteredPrizes = prizes.filter((prize) => prize.value);
    let text = "";
    filteredPrizes.forEach((prize, index) => {
      text = (index > 0 ? "&" : "") + prize.value + " " + prize.type;
    });
    return text;
  };

  return (
    <div className="gameplay-page">
      <Background
        type={
          startGameData && startGameData.data?.question_category?.code
            ? startGameData.data.question_category?.code
            : "defaultGame"
        }
        category={startGameData?.data?.question_category?.gradient_background}
      >
        <NavBar hideMenu slotEnd="lives" />
        <GameplayLayout
          timer={currentTimeRef.current}
          prizes={
            <PrizeQuestion
              icon="diamonds"
              category={startGameData?.data?.question_category?.icon || ""}
            >
              {300}
            </PrizeQuestion>
          }
          question={
            question?.content[lang] ??
            (question?.content.en || "Loading question...")
          }
          players={[
            <Player
              key="me"
              avatar={user?.character.avatar}
              playerType={
                results
                  ? (cx({
                      success:
                        results.answer.correct &&
                        results.opponent_answer.correct
                          ? results.answer.answer_time <
                            results.opponent_answer.answer_time
                          : results.answer.correct,
                      defeat:
                        !results.answer.correct ||
                        (results.answer.correct &&
                          results.opponent_answer.correct &&
                          results.answer.answer_time >
                            results.opponent_answer.answer_time),
                    }) as PlayerProps["playerType"])
                  : "primary"
              }
              left={
                results ? (
                  (results.answer.correct &&
                    !results.opponent_answer.correct) ||
                  (results.answer.correct &&
                    results.opponent_answer.correct &&
                    results.answer.answer_time <
                      results.opponent_answer.answer_time) ? (
                    <img
                      src="/img/icns/answer-correct.svg"
                      className="icon-input"
                      alt=""
                    />
                  ) : !results.answer.correct ? (
                    <img
                      src="/img/icns/answer-wrong.svg"
                      className="icon-input"
                      alt=""
                    />
                  ) : undefined
                ) : undefined
              }
              right={
                results && results.answer.correct ? (
                  <Badge
                    type={
                      cx({
                        success:
                          results.answer.correct &&
                          results.opponent_answer.correct
                            ? results.answer.answer_time <
                              results.opponent_answer.answer_time
                            : results.answer.correct,
                        danger:
                          !results.answer.correct ||
                          (results.answer.correct &&
                            results.opponent_answer.correct &&
                            results.answer.answer_time >
                              results.opponent_answer.answer_time),
                      }) as BadgeProps["type"]
                    }
                    aspect="rectangle"
                  >
                    {timer &&
                      formatAnswerTime(
                        Math.min(results.answer.answer_time, timer * 1000)
                      )}
                  </Badge>
                ) : undefined
              }
            />,
            opponent ? (
              <Player
                key="opponent"
                avatar={opponent?.avatar}
                playerType={
                  results && opponentAnswered
                    ? (cx({
                        success:
                          results.opponent_answer.correct &&
                          results.answer.correct
                            ? results.opponent_answer.answer_time <
                              results.answer.answer_time
                            : results.opponent_answer.correct,
                        defeat:
                          !results.opponent_answer.correct ||
                          (results.opponent_answer.correct &&
                            results.answer.correct &&
                            results.opponent_answer.answer_time >
                              results.answer.answer_time),
                      }) as PlayerProps["playerType"])
                    : "primary"
                }
                left={
                  results &&
                  opponentAnswered &&
                  results.opponent_answer.correct ? (
                    <Badge
                      type={
                        cx({
                          success:
                            results.opponent_answer.correct &&
                            results.answer.correct
                              ? results.opponent_answer.answer_time <
                                results.answer.answer_time
                              : results.opponent_answer.correct,
                          danger:
                            !results.opponent_answer.correct ||
                            (results.opponent_answer.correct &&
                              results.answer.correct &&
                              results.opponent_answer.answer_time >
                                results.answer.answer_time),
                        }) as BadgeProps["type"]
                      }
                      aspect="rectangle"
                    >
                      {formatAnswerTime(results.opponent_answer.answer_time)}
                    </Badge>
                  ) : undefined
                }
                right={
                  results && opponentAnswered ? (
                    (results.opponent_answer.correct &&
                      !results.answer.correct) ||
                    (results.opponent_answer.correct &&
                      results.answer.correct &&
                      results.opponent_answer.answer_time <
                        results.answer.answer_time) ? (
                      <img
                        src="/img/icns/answer-correct.svg"
                        className="icon-input"
                        alt=""
                      />
                    ) : !results.opponent_answer.correct ? (
                      <img
                        src="/img/icns/answer-wrong.svg"
                        className="icon-input"
                        alt=""
                      />
                    ) : undefined
                  ) : undefined
                }
              />
            ) : (
              <div className="player-container">
                <LoadingOpponent />
              </div>
            ),
          ]}
        >
          {/* If the options are type image add class .options-images on the .options div */}
          <div
            className={cx("options", {
              "options-images": isImageQuestion(),
            })}
          >
            {question?.answers?.map((answer) => {
              const index = answer.index;
              const key = `${answer.type}-${index}`;
              const status = correctAnswer
                ? (cx({
                    correct: correctAnswer.correct_answer_index === index,
                    wrong:
                      correctAnswer.correct_answer_index !== index &&
                      (playerAnswer?.answer_index === index ||
                        (opponentAnswer?.answer_index === index &&
                          opponentAnswered)),
                  }) as "correct" | "wrong") ?? "default"
                : "default";
              if (answer.type === "text") {
                return (
                  <GameplayAnswer
                    key={key}
                    gameplay="text"
                    status={status}
                    player={getAnswerType(index)}
                    options={{
                      text: answer.value[lang] || answer.value.en,
                      badgeType: "outer",
                    }}
                    onClick={() => handleAnswer(index)}
                    disabled={!!correctAnswer}
                  />
                );
              } else if (answer.type === "image") {
                return (
                  <GameplayAnswer
                    key={key}
                    gameplay="image"
                    player={getAnswerType(index)}
                    status={status}
                    options={{ imageSrc: answer.value }}
                    onClick={() => handleAnswer(index)}
                    disabled={!!correctAnswer}
                  />
                );
              }
            })}
          </div>
        </GameplayLayout>
      </Background>
      <AnimatePresence>
        {showInfo && results && (
          <motion.div
            variants={bonusResultContainerVariants}
            initial={"hidden"}
            animate={"visible"}
            exit={"hidden"}
            className="modal-presence-wrapper"
          >
            <Modal
              show={showInfo}
              setShow={handleContinue}
              hideCloseButton
              defaultAnimations={false}
              disableClickOutside
            >
              {/* @TODO: @VladS, Adrian please fix the modal so it shows and redirects properly */}
              <BonusResultModal
                bonusCategory="music"
                subtitle={game?.data?.bonus_game?.title[lang] || ""}
                title={
                  results.won && results.answer.correct ? "You won" : "You Lost"
                }
                awardQuantityLives={results.prizes?.lives || 0}
                awardQuantityPoints={results.prizes?.points || 0}
                prizeImage={results.prizes?.prize?.image}
                prizeText={results.prizes?.prize?.name[lang]}
                text={
                  results.won && results.answer.correct
                    ? `You have just won`
                    : "Move faster next time"
                }
                boldText={
                  results.won && results.answer.correct
                    ? generatePrizeDescription([
                        { type: "lives", value: results?.prizes?.lives },
                        { type: "diamonds", value: results?.prizes?.points },
                        { type: "", value: results?.prizes?.prize?.name[lang] },
                      ])
                    : ""
                }
                buttonText={
                  results.won && results.answer.correct ? "Collect" : "Continue"
                }
                iconSlotLeft={
                  results.prizes.lives
                    ? "lives"
                    : results.prizes.points
                    ? "diamonds"
                    : undefined
                }
                iconSlotRight={
                  results.prizes.points
                    ? "diamonds"
                    : results.prizes.lives
                    ? "lives"
                    : undefined
                }
                mainIcon={
                  results.won && results.answer.correct ? "ribon" : "lost"
                }
                onClick={() => handleContinue(false)}
              />
            </Modal>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default BonusGameplay;
